import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, X } from "lucide-react";
import { api, shortDate } from "../../lib/api";
import { apiError } from "../../lib/auth";

const SEV = { high: "border-[#FF003C] text-[#FF6B8A]", medium: "border-[#FFB800] text-[#FFB800]", low: "border-white/30 text-white/60", info: "border-[#00E5FF]/40 text-[#00E5FF]" };
const ACT = { block_customer: "Approve: block customer", block_ip: "Approve: block IP 30 days", lock_user: "Approve: lock account" };

export const Findings = ({ items, reload, canAct, admin, testid = "guard-findings", max = 50 }) => {
  const act = async (f) => {
    if (!window.confirm(`${ACT[f.action.type]} → ${f.action.value}?`)) return;
    try { const { data } = await api.post("/guard/act", { ...f.action, finding_id: f.id }); toast.success(data.detail); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const dismiss = async (f) => { try { await api.post("/guard/dismiss", { id: f.id }); reload(); } catch (e) { toast.error(apiError(e)); } };
  if (!items.length) return <p className="flex items-center gap-2 text-sm text-white/55" data-testid={`${testid}-empty`}><ShieldCheck className="w-4 h-4 text-[#00E5FF]" />No fraud or threats found.</p>;
  return (
    <ul className="space-y-2" data-testid={testid}>
      {items.slice(0, max).map((f, i) => (
        <li key={f.id} className={`border-l-2 pl-3 py-1.5 ${SEV[f.severity].split(" ")[0]}`} data-testid={`${testid}-item-${i}`}>
          <div className="flex items-start gap-2">
            <ShieldAlert className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${SEV[f.severity].split(" ")[1]}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate">{f.title}</p>
              <p className="text-xs text-white/50 break-words">{f.detail}</p>
              <p className="font-mono text-[9px] tracking-widest text-white/30 mt-0.5 uppercase">{f.kind.replace(/_/g, " ")} · {f.severity}</p>
              {canAct && (
                <div className="flex gap-3 mt-1">
                  {f.action && (f.action.type !== "block_ip" || admin) && <button onClick={() => act(f)} className="font-mono text-[10px] text-[#FF6B8A] hover:text-white" data-testid={`${testid}-approve-${i}`}>{ACT[f.action.type]}</button>}
                  <button onClick={() => dismiss(f)} className="font-mono text-[10px] text-white/40 hover:text-white" data-testid={`${testid}-dismiss-${i}`}>Dismiss</button>
                </div>
              )}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export const Events = ({ items, testid = "guard-events" }) => (
  <ul className="divide-y divide-white/5 text-sm" data-testid={testid}>
    {!items.length && <li className="text-white/45 py-2">No security events in the last 24 hours.</li>}
    {items.map((e) => (
      <li key={e.id} className="py-2 flex gap-3" data-testid={`${testid}-${e.id}`}>
        <span className={`font-mono text-[9px] uppercase w-16 shrink-0 ${SEV[e.severity]?.split(" ")[1] || "text-white/50"}`}>{e.kind.replace(/_/g, " ")}</span>
        <span className="flex-1 min-w-0 text-white/75 break-words">{e.detail}<span className="block text-[10px] text-white/35">{e.ip} · {e.action}</span></span>
        <span className="text-[10px] text-white/35 shrink-0">{shortDate(e.ts)}</span>
      </li>
    ))}
  </ul>
);

export const Blocks = ({ items, reload }) => {
  const unblock = async (ip) => { await api.post("/guard/act", { type: "unblock_ip", value: ip }); toast.success(`${ip} unblocked`); reload(); };
  return (
    <ul className="space-y-1 text-sm" data-testid="guard-blocks">
      {!items.length && <li className="text-white/45">No IPs blocked right now.</li>}
      {items.map((b) => (
        <li key={b.ip} className="flex items-center gap-3" data-testid={`guard-block-${b.ip}`}>
          <span className="font-mono text-xs">{b.ip}</span><span className="text-xs text-white/45 flex-1 truncate">{b.reason} · until {shortDate(b.until)}</span>
          <button onClick={() => unblock(b.ip)} className="text-white/45 hover:text-white" title="Unblock" data-testid={`guard-unblock-${b.ip}`}><X className="w-3.5 h-3.5" /></button>
        </li>
      ))}
    </ul>
  );
};
